import { useMemo } from "react";
import type { QuoteSummary } from "../types/models";
import { QuoteDetailsEntryStatus } from "~/domains/orders/types/GetItemStatus";

type QuoteComputedData = {
  confirmedQtyMap: Record<number, number>;
  positionOptions: Map<string, number[] | undefined>;
  materialNumberOptions: (string | undefined)[];
  quoteDetails: QuoteSummary | undefined;
};

export function useQuoteComputedData(filteredQuoteList: { quoteNumber: string }[], actualQuoteDetails: QuoteSummary[]) {
  return useMemo(() => {
    const quoteDetailsMap = new Map(actualQuoteDetails.map((q) => [q.quoteNumber, q]));

    const computedMap = new Map<string, QuoteComputedData>();

    filteredQuoteList.forEach((quote) => {
      const quoteDetails = quoteDetailsMap.get(quote.quoteNumber);

      const mainQuotes =
        quoteDetails?.quotesDetails.products.flatMap((p) => [
          ...(p.mainQuotes || []).filter((q) => q.statusCode === QuoteDetailsEntryStatus.RELEASED),
          ...(p.alternativeQuotes || []).flatMap((a) =>
            (a.linesItem || []).filter((q) => q.statusCode === QuoteDetailsEntryStatus.RELEASED),
          ),
        ]) ?? [];

      const confirmedQtyMap = mainQuotes.reduce<Record<number, number>>((acc, item) => {
        const lineNumber = item.lineNumber;
        acc[lineNumber] = (acc[lineNumber] ?? 0) + (item.confirmedQty ?? 0);
        return acc;
      }, {});

      const materialNumberOptions = [
        ...new Set(quoteDetails?.quotesDetails.products.map((q) => q.product.sapMaterialNumber + q.packCode)),
      ].filter((quote) => quote !== "RFQundefined");

      const positionOptions = new Map<string, number[] | undefined>();

      materialNumberOptions.forEach((key) => {
        const product = quoteDetails?.quotesDetails.products.find(
          (quote) => key == quote.product.sapMaterialNumber + quote.packCode,
        );

        const options = [
          ...new Set(product?.mainQuotes.map((quote) => quote.lineNumber)),
          ...new Set(product?.alternativeQuotes?.map((quote) => quote.lineNumber)),
        ];
        if (key) {
          positionOptions.set(key, options);
        }
      });

      computedMap.set(quote.quoteNumber, {
        confirmedQtyMap,
        positionOptions,
        materialNumberOptions,
        quoteDetails,
      });
    });

    return computedMap;
  }, [actualQuoteDetails]);
}
